import { createContext, useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  IAuthorizationApi,
  NotImplementedAuthorizationApi,
} from '../../api/authorization/IAuthorizationApi';
import {
  ICompanyApi,
  NotImplementedCompanyApi,
} from '../../api/company/ICompanyApi';
import { createApi } from '../../api/createApi';
import {
  IFeedbackApi,
  NotImplementedFeedbackApi,
} from '../../api/feedback/IFeedbackApi';
import {
  IGamificationApi,
  NotImplementedGamificationApi,
} from '../../api/gamification/IGamificationApi';
import { ITeamApi, NotImplementedTeamApi } from '../../api/team/ITeamApi';
import { IUserApi, NotImplementedUserApi } from '../../api/user/IUserApi';
import { Config } from '../../config';
import { useErrorHandler } from '../ErrorHandlerContext/useErrorHandler';
import { useStorageProvider } from '../StorageProviderContext/useStorageProvider';
import {
  ICompanyActionApi,
  NotImplementedCompanyActionApi,
} from '../../api/companyAction/ICompanyActionApi';
import {
  IAlertsApi,
  NotImplementedAlertsApi,
} from '../../api/alerts/IAlertsApi';
import { FC } from '../../types/general.types';
import { ILumiaApi, NotImplementedLumiaApi } from '../../api/lumia/ILumiaApi';
import { setIsAuthorized } from '../../redux/general/general.slice';
import { useClearCache } from '../../hooks/api/utils';

export type ApiContextProps = {
  authApi: IAuthorizationApi;
  userApi: IUserApi;
  companyApi: ICompanyApi;
  teamApi: ITeamApi;
  feedbackApi: IFeedbackApi;
  gamificationApi: IGamificationApi;
  companyActionApi: ICompanyActionApi;
  alertApi: IAlertsApi;
  lumiaApi: ILumiaApi;
  lumiaApi2: ILumiaApi;
};

export const ApiContext = createContext<ApiContextProps>({
  authApi: new NotImplementedAuthorizationApi(),
  userApi: new NotImplementedUserApi(),
  companyApi: new NotImplementedCompanyApi(),
  teamApi: new NotImplementedTeamApi(),
  feedbackApi: new NotImplementedFeedbackApi(),
  gamificationApi: new NotImplementedGamificationApi(),
  companyActionApi: new NotImplementedCompanyActionApi(),
  alertApi: new NotImplementedAlertsApi(),
  lumiaApi: new NotImplementedLumiaApi(),
  lumiaApi2: new NotImplementedLumiaApi(),
});

export const ApiProvider: FC = ({ children }) => {
  const storageProvider = useStorageProvider();
  const errorHandler = useErrorHandler();
  const dispatch = useDispatch();
  const clearCache = useClearCache();
  const [isLoading, setIsLoading] = useState(true);

  const [authApi, setAuthApi] = useState<IAuthorizationApi>(
    new NotImplementedAuthorizationApi()
  );
  const [userApi, setUserApi] = useState<IUserApi>(new NotImplementedUserApi());
  const [companyActionApi, setCompanyActionApi] = useState<ICompanyActionApi>(
    new NotImplementedCompanyActionApi()
  );
  const [companyApi, setCompanyApi] = useState<ICompanyApi>(
    new NotImplementedCompanyApi()
  );
  const [teamApi, setTeamApi] = useState<ITeamApi>(new NotImplementedTeamApi());
  const [feedbackApi, setFeedbackApi] = useState<IFeedbackApi>(
    new NotImplementedFeedbackApi()
  );
  const [gamificationApi, setGamificationApi] = useState<IGamificationApi>(
    new NotImplementedGamificationApi()
  );
  const [alertApi, setAlertApi] = useState<IAlertsApi>(
    new NotImplementedAlertsApi()
  );
  const [lumiaApi, setLumiaApi] = useState<ILumiaApi>(
    new NotImplementedLumiaApi()
  );
  const [lumiaApi2, setLumiaApi2] = useState<ILumiaApi>(
    new NotImplementedLumiaApi()
  );

  const handleAuthenticationFailed = useCallback(() => {
    clearCache();
    dispatch(setIsAuthorized(false));
  }, [clearCache, dispatch]);

  useEffect(() => {
    createApi({
      baseUrl: Config.baseUrl!,
      storageProvider,
      errorHandler,
      onAuthenticationFailed: handleAuthenticationFailed,
    }).then(
      ({
        authorization,
        userApi,
        companyApi,
        teamApi,
        feedbackApi,
        gamificationApi,
        companyActionApi,
        alertApi,
        lumiaApi,
        lumiaApi2,
      }) => {
        setAuthApi(authorization);
        setUserApi(userApi);
        setCompanyApi(companyApi);
        setTeamApi(teamApi);
        setFeedbackApi(feedbackApi);
        setGamificationApi(gamificationApi);
        setCompanyActionApi(companyActionApi);
        setAlertApi(alertApi);
        setLumiaApi(lumiaApi);
        setLumiaApi2(lumiaApi2);
        setIsLoading(false);
      }
    );
  }, [errorHandler, storageProvider, handleAuthenticationFailed]);

  return (
    <ApiContext.Provider
      value={{
        authApi,
        userApi,
        companyApi,
        teamApi,
        feedbackApi,
        gamificationApi,
        companyActionApi,
        alertApi,
        lumiaApi,
        lumiaApi2,
      }}
    >
      {isLoading ? null : children}
    </ApiContext.Provider>
  );
};
